import { makeStyles, shorthands } from "@fluentui/react-components";
import { designTokens } from "theme/customTheme";

const SEARCH_ICON_LEFT_OFFSET = "12px";
const SEARCH_INPUT_PADDING_WITH_ICON = "9px 13px 9px 36px";

export const useDecoodaReviewsStyles = makeStyles({
  highlightedText: {
    backgroundColor: designTokens.semantic.warningLight,
    whiteSpace: "pre-wrap",
  },
  normalText: {
    whiteSpace: "pre-wrap",
  },
  centeredContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: designTokens.spacing[12],
  },
  centeredColumnContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: designTokens.spacing[4],
    padding: designTokens.spacing[12],
  },
  centeredColumnContainerSmallGap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: designTokens.spacing[2],
    padding: designTokens.spacing[12],
  },
  fadedText: {
    opacity: 0.7,
  },
  impactAnalysisContainer: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[6],
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[6],
  },
  thresholdInputRow: {
    display: "flex",
    gap: designTokens.spacing[1],
  },
  thresholdInput: {
    width: "150px",
  },
  chartsContainer: {
    width: "100%",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
  },
  chartWrapper: {
    width: "700px",
    height: "500px",
    overflow: "auto",
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    borderRadius: designTokens.borderRadius.sm,
  },

  atdContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[6],
  },
  topicsWrapper: {
    position: "relative",
  },
  topicsContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: designTokens.spacing[1],
  },
  topicsContainerDimmed: {
    display: "flex",
    flexWrap: "wrap",
    gap: designTokens.spacing[1],
    opacity: 0.5,
    pointerEvents: "none",
  },
  topicButtonBase: {
    borderRadius: designTokens.borderRadius.full,
    transitionProperty: "background-color, border-color, color, opacity",
    transitionDuration: "150ms",
    transitionTimingFunction: "ease",
    ...shorthands.border("2px", "solid", "transparent"),
    "@media (prefers-reduced-motion: reduce)": {
      transitionDuration: "0ms",
    },
  },
  topicButtonPositive: {
    color: designTokens.semantic.success,
  },
  topicButtonNegative: {
    color: designTokens.semantic.error,
  },
  topicButtonPositiveFilled: {
    backgroundColor: "rgba(47, 158, 110, 0.12)",
    color: "#1e8a51",
    border: "1px solid rgba(47, 158, 110, 0.28)",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.bold,
  },
  topicButtonNegativeFilled: {
    backgroundColor: "rgba(194, 85, 63, 0.12)",
    color: "#c2553f",
    border: "1px solid rgba(194, 85, 63, 0.28)",
    paddingTop: "5px",
    paddingBottom: "5px",
    paddingLeft: "12px",
    paddingRight: "12px",
    fontSize: "12.5px",
    fontWeight: designTokens.fontWeight.bold,
  },
  topicButtonSelected: {
    backgroundColor: designTokens.primary[100],
  },
  topicButtonDragOver: {
    backgroundColor: designTokens.semantic.successLight,
    ...shorthands.border("2px", "dashed", designTokens.semantic.success),
  },
  topicButtonDragging: {
    opacity: 0.5,
  },
  topicButtonMerging: {
    cursor: "wait",
  },
  topicButtonGrab: {
    cursor: "grab",
  },
  mergeOverlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: designTokens.overlay.light,
    borderRadius: designTokens.borderRadius.md,
    zIndex: 10,
  },
  mergeOverlayContent: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: designTokens.spacing[2],
  },
  mergeOverlayText: {
    fontWeight: designTokens.fontWeight.medium,
  },
  reviewsTable: {
    minWidth: "510px",
  },
  menuList: {
    minWidth: "140px",
  },
  cellColumnLayout: {
    display: "flex",
    gap: designTokens.spacing[1],
    flexDirection: "column",
  },
  dialogContent: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },
  dialogContentLargeGap: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[4],
  },
  dialogSectionSmallGap: {
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[2],
  },
  mergeIntoLabel: {
    fontWeight: designTokens.fontWeight.semibold,
  },
  mergeTargetText: {
    fontStyle: "italic",
  },
  mergeTopicList: {
    maxHeight: "200px",
    overflowY: "auto",
    ...shorthands.border("1px", "solid", designTokens.neutral[200]),
    borderRadius: designTokens.borderRadius.sm,
    padding: designTokens.spacing[2],
  },
  mergeTopicItem: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    ...shorthands.padding(designTokens.spacing[1], "0"),
  },
  noTopicsFound: {
    color: designTokens.neutral[500],
    fontStyle: "italic",
  },
  selectedCount: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
  },

  // Two-card layout (showCards mode)
  atdTopicsCard: {
    backgroundColor: designTokens.neutral.white,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.lg,
    boxShadow: `0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px 0 rgba(0,0,0,0.04)`,
    padding: designTokens.spacing[5],
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },
  atdReviewsCard: {
    backgroundColor: designTokens.neutral.white,
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.lg,
    boxShadow: `0 1px 3px 0 rgba(0,0,0,0.07), 0 1px 2px 0 rgba(0,0,0,0.04)`,
    padding: designTokens.spacing[5],
    display: "flex",
    flexDirection: "column",
    gap: designTokens.spacing[3],
  },
  atdCardHeaderRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: designTokens.spacing[2],
    marginBottom: designTokens.spacing[1],
  },
  atdCardTitle: {
    fontSize: designTokens.fontSize["2xl"],
    fontWeight: designTokens.fontWeight.bold,
    color: designTokens.neutral[900],
    margin: "0",
    lineHeight: designTokens.lineHeight.tight,
  },
  atdDescription: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    margin: "0",
    lineHeight: "1.5",
  },
  atdFilterStatus: {
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    fontStyle: "italic",
    margin: "0",
  },
  atdReviewsLabel: {
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
    letterSpacing: designTokens.letterSpacing.wide,
    textTransform: "uppercase" as const,
    color: designTokens.neutral[500],
    margin: "0",
  },
  atdSearchWrapper: {
    position: "relative",
  },
  atdSearchIcon: {
    position: "absolute",
    left: SEARCH_ICON_LEFT_OFFSET,
    top: "50%",
    transform: "translateY(-50%)",
    color: designTokens.neutral[400],
    pointerEvents: "none",
    fontSize: designTokens.iconSize.sm,
  },
  atdSearchInput: {
    width: "100%",
    boxSizing: "border-box",
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.md,
    padding: SEARCH_INPUT_PADDING_WITH_ICON,
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[900],
    backgroundColor: designTokens.neutral.white,
    outline: "none",
    fontFamily: "inherit",
    ":focus": {
      border: `1px solid ${designTokens.primary[500]}`,
    },
  },

  // ATD card table styles (showCards mode only)
  highlightedTextTeal: {
    backgroundColor: "rgba(3, 159, 184, 0.16)",
    borderRadius: "3px",
    paddingLeft: "2px",
    paddingRight: "2px",
    boxShadow: "inset 0 -2px 0 rgba(3, 159, 184, 0.45)",
    whiteSpace: "pre-wrap",
  },
  atdTableCard: {
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.lg,
    boxShadow: "0 1px 2px 0 rgba(0,0,0,.05)",
    overflow: "hidden",
  },
  atdTableHeaderCell: {
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[500],
    textTransform: "uppercase" as const,
    letterSpacing: designTokens.letterSpacing.wide,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: designTokens.spacing[3],
    paddingRight: "0",
    backgroundColor: designTokens.neutral[50],
    whiteSpace: "nowrap" as const,
  },
  atdTableRow: {
    borderBottom: `1px solid ${designTokens.neutral[200]}`,
    ":hover": {
      backgroundColor: "#f6f7f9",
    },
  },
  atdTableCell: {
    paddingTop: designTokens.spacing[3],
    paddingBottom: designTokens.spacing[3],
    paddingLeft: designTokens.spacing[3],
    paddingRight: "0",
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[600],
    verticalAlign: "top",
    lineHeight: designTokens.lineHeight.normal,
  },
  atdTableTopicsStack: {
    display: "flex",
    flexDirection: "column",
    gap: "3px",
  },
  atdTableMonoCell: {
    fontFamily: "ui-monospace, Consolas, monospace",
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
  },
  atdTableCellReviewWide: {
    paddingTop: designTokens.spacing[3],
    paddingBottom: designTokens.spacing[3],
    paddingLeft: designTokens.spacing[3],
    paddingRight: "0",
    fontSize: designTokens.fontSize.sm,
    color: designTokens.neutral[600],
    verticalAlign: "top",
    lineHeight: designTokens.lineHeight.normal,
    flex: "2 1 0",
  },
  atdTableHeaderCellReviewWide: {
    fontSize: designTokens.fontSize.xxs,
    fontWeight: designTokens.fontWeight.semibold,
    color: designTokens.neutral[500],
    textTransform: "uppercase" as const,
    letterSpacing: designTokens.letterSpacing.wide,
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: designTokens.spacing[3],
    paddingRight: "0",
    backgroundColor: designTokens.neutral[50],
    whiteSpace: "nowrap" as const,
    flex: "2 1 0",
  },
  atdTableFoot: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "14px",
    paddingRight: "14px",
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
    borderTop: `1px solid ${designTokens.neutral[200]}`,
    backgroundColor: designTokens.neutral[50],
  },
  atdTableFootPagination: {
    display: "flex",
    alignItems: "center",
    gap: designTokens.spacing[2],
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[500],
  },
  atdTableFootBtn: {
    background: "none",
    border: `1px solid ${designTokens.neutral[200]}`,
    borderRadius: designTokens.borderRadius.sm,
    paddingTop: designTokens.spacing[1],
    paddingBottom: designTokens.spacing[1],
    paddingLeft: designTokens.spacing[2],
    paddingRight: designTokens.spacing[2],
    cursor: "pointer",
    fontSize: designTokens.fontSize.xs,
    color: designTokens.neutral[600],
    lineHeight: "1",
    ":hover": {
      backgroundColor: designTokens.neutral[50],
    },
    ":disabled": {
      opacity: "0.4",
      cursor: "not-allowed",
    },
  },
});
